import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import PhysicalSample from '../../../../../types/physical-sample/PhysicalSample';
import Studio from '../../../../../types/studio/Studio';
import StudioWriteDTO from '../../../../../types/studio/StudioWriteDTO';
import { IsAdminDirective } from '../../../directives/is-admin.directive';
import { EnumLabelPipe } from '../../../pipes/enum-label.pipe';
import { FormatDateTime } from '../../../pipes/format-date-time';
import { RoleNamePipe } from '../../../pipes/role-name.pipe';
import { PhysicalSampleService } from '../../../services/physical-sample.service';
import { StudioService } from '../../../services/studio.service';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import { UpdateStudioDialogComponent } from '../../shared/studios/update-studio-dialog/update-studio-dialog.component';

@Component({
    selector: 'app-studio-detail',
    templateUrl: './studio-detail.component.html',
    styleUrls: [ './studio-detail.component.scss' ],
    imports: [
        RouterLink,
        PageHeaderComponent,
        UpdateStudioDialogComponent,
        Button,
        Tag,
        Tooltip,
        TableModule,
        FormatDateTime,
        RoleNamePipe,
        IsAdminDirective,
        EnumLabelPipe
    ]
})
export class StudioDetailComponent implements OnInit {
    public studio: Studio | null = null;
    public editableStudio: StudioWriteDTO = new StudioWriteDTO({});
    public displayUpdateDialog = false;

    public physicalSamples: PhysicalSample[] = [];
    public physicalSamplesTotalRecords = 0;
    public physicalSamplesRows = 10;
    public loadingPhysicalSamples = false;

    constructor(
        private route: ActivatedRoute,
        private studioService: StudioService,
        private physicalSampleService: PhysicalSampleService,
        private messageService: MessageService
    ) {
    }

    ngOnInit(): void {
        this.loadStudio();
    }

    public onPhysicalSamplesPageChange(event: any): void {
        const page = Math.floor(event.first / event.rows);
        this.physicalSamplesRows = event.rows;
        this.loadPhysicalSamples(page);
    }

    public openEditDialog(): void {
        this.editableStudio = new StudioWriteDTO(this.studio ?? {});
        this.editableStudio.dealershipId = this.studio?.dealership?.id;
        this.displayUpdateDialog = true;
    }

    public reloadStudioDetails(): void {
        this.loadStudio();
    }

    public deleteStudio(): void {
        if (!this.studio) {
            return;
        }

        const id = this.studio.id;
        this.studioService
            .deleteStudio(id)
            .then(() => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Studio deleted successfully',
                    life: 3000,
                });
                window.history.back(); // Navigate back after deletion
            })
            .catch((error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000,
                });
            });
    }

    private loadPhysicalSamples(page: number = 0): void {
        if (!this.studio?.id) {
            return;
        }
        this.loadingPhysicalSamples = true;
        this.physicalSampleService
            .getPhysicalSamples(
                undefined, page, this.physicalSamplesRows, undefined, undefined, undefined, [ this.studio.id ])
            .then(result => {
                this.physicalSamples = result.content;
                this.physicalSamplesTotalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000,
                });
            })
            .finally(() => {
                this.loadingPhysicalSamples = false;
            });
    }

    private loadStudio(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            const idNumber = parseInt(id);
            this.studioService
                .getStudio(idNumber)
                .then((result: Studio) => {
                    this.studio = result;
                    this.loadPhysicalSamples(0);
                })
                .catch((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000,
                    });
                });
        }
    }
}
